import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { ButtonData } from '../../../../components/buttonGroup/ButtonGroup';
import { DetailSection, LabelText } from '../../../../components/generic';
import CurrencyCell from '../../../../components/tables/cellComponents/CurrencyCell';
import { LinkCell } from '../../../../components/tables/cellComponents/LinkCell';
import { DateFilterField } from '../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../components/tables/filterForms/FormSelect';
import { SearchField } from '../../../../components/tables/filterForms/SearchField';
import SFCTable from '../../../../components/tables/SFCTable';
import { useToast } from '../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../hooks';
import { useGetAllOrdersQuery } from '../../../../services/endpoints/orders/orders';
import { useGetAllPromotionalCodesQuery } from '../../../../services/endpoints/orders/promotionalCode';
import { useGetAllParentsQuery } from '../../../../services/endpoints/people/parent';
import { Scopes, User } from '../../../../services/endpoints/people/user';
import { useGetAllLocationsSimpleQuery } from '../../../../services/endpoints/places/location';
import { useGetCoursesSimpleQuery } from '../../../../services/endpoints/schedule/course';
import { getCurrentUser } from '../../../../services/helper';
import { Course } from '../../../../types/course.type';
import { Order, PromotionalCode, Refund } from '../../../../types/orders.type';
import { formatDate } from '../../../../utils/dateUtils';
import {Forbidden} from "../../../../components/error/Forbidden";
import {
  getFilterValue,
  parseCreatedAtLocationDeepLinkFilters
} from '../../../../utils/listFilterSearchParams';
import { canSeeFinanceSummary } from '../../../../utils/rbac';

const defaultScope = 'completed';

const parentDropdownQuery = {
  data: { entries: 500000, orderBy: { field: 'first_name', order: 'asc' } }
};

const sessionDropdownQuery = {
  data: { entries: 100000, orderBy: { field: 'title', order: 'asc' } }
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Match weekly report: gte = start-of-day UTC; lte = start of next day UTC (inclusive end date). */
export const normalizeCreatedAtFilterValue = (operator: string, value: string): string => {
  if (!value || !DATE_ONLY.test(value)) return value;

  const date = new Date(`${value}T00:00:00.000Z`);
  if (operator === 'greater_equal_than') {
    return date.toISOString();
  }
  if (operator === 'lesser_equal_than') {
    date.setUTCDate(date.getUTCDate() + 1);
    return date.toISOString();
  }
  return value;
};

/** Convert API/URL datetime back to YYYY-MM-DD for the date input. */
export const toCreatedAtDateInputValue = (operator: string, value?: string): string => {
  if (!value) return '';
  if (DATE_ONLY.test(value)) return value;

  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  if (operator === 'lesser_equal_than') {
    date.setUTCDate(date.getUTCDate() - 1);
  }
  return date.toISOString().split('T')[0];
};

// RBAC-FE-ORDERS: Create orders - matches backend isExclusivelyParentLocationAdminOrSuper
// Note: Admin and Global are intentionally excluded from creating orders in admin UI
// Parents create orders through public checkout flow
export const userCanCreate_Orders = (user: User | null) =>
    !!(user?.isSuper || user?.isSupremeAdmin || user?.isLocationAdmin);

// RBAC-FE-ORDERS: View/Remove/Submit orders - matches backend isNotEditor
// All roles except Editor and Customer can view orders
export const userCanViewRemoveSubmit_Orders = (user: User | null) =>
    !!(user?.isSuper ||
       user?.isSupremeAdmin ||
       user?.isAdmin ||
       user?.isGlobal ||
       user?.isLocationAdmin ||
       user?.isInstructor);

export const getOrdersColumns = (scope?: string): ColumnDef<Order>[] => [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ row }) => (
      <LinkCell
        content={row.original.id.toString()}
        url={`/admin/orders/orders/${row.original.id}`}
      />
    )
  },
  {
    header: 'Parent',
    accessorKey: 'parent_id',
    cell: ({ row }) => (
      <LinkCell
        content={`${row.original.parent.first_name} ${row.original.parent.last_name}`}
        url={`/admin/sfc-users/parents/${row.original.parent_id}`}
      />
    ),
    enableSorting: false
  },
  {
    header: 'Children',
    accessorKey: 'child_full_name',
    cell: ({ row }) =>
      !row.original.registrations?.length ? (
        ''
      ) : (
        <Stack>
          {row.original.registrations.map((item) => (
            <LinkCell
              content={`${item.child?.first_name} ${item.child?.last_name}`}
              url={`/admin/sfc-users/children/${item.child_id}`}
            />
          ))}
        </Stack>
      ),
    enableSorting: false
  },
  {
    header: 'Session',
    accessorKey: 'course_id',
    cell: ({ row }) =>
      !row.original.course ? (
        ''
      ) : (
        <LinkCell
          content={row.original.course.title}
          url={`/admin/schedule/sessions/${row.original.course_id}`}
        />
      ),
    enableSorting: false
  },
  {
    header: 'Email',
    accessorKey: 'parent_email',
    cell: ({ row }) => row.original.parent.email,
    enableSorting: false
  },
  {
    header: scope === 'incomplete' ? 'Amount' : 'Amount Paid',
    accessorKey: 'total',
    cell: ({ getValue }) => <CurrencyCell value={getValue<number>()} />,
    enableSorting: false
  },
  {
    header: 'Refunds',
    accessorKey: 'refunds',
    enableSorting: false,
    cell: ({ getValue }) => (
      <>
        {getValue<Refund[]>().map((r, index) => (
          <CurrencyCell
            key={index}
            value={r.total}
            label={`on ${formatDate(new Date(r.created_at), 'MM/dd/yyyy')}`}
          />
        ))}
      </>
    )
  },
  {
    header: 'Created At',
    accessorKey: 'created_at',
    cell: ({ row }) => formatDate(new Date(row.original.created_at), 'MM/dd/yyyy h:mm zzz')
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/orders/orders/${row.original.id}`}
          underline
        />
      </Stack>
    )
  }
];

export const OrdersView = () => {
  const currentUser = getCurrentUser();
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl(
    { scope: defaultScope, data: { entries: 10, page: 1 } },
    { parseExtra: parseCreatedAtLocationDeepLinkFilters }
  );
  const [scopes, setScopes] = useState<Scopes>({});
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const canAccessOrders = userCanViewRemoveSubmit_Orders(currentUser);
  
  const { data, error, isLoading, isFetching } = useGetAllOrdersQuery(filters, {
    skip: !canAccessOrders
  });
  const [orders, setOrders] = useState<Order[]>([]);
  const { data: coursesData, error: coursesError } = useGetCoursesSimpleQuery(sessionDropdownQuery, {
    skip: !canAccessOrders
  });
  const { data: parentData, error: parentError } = useGetAllParentsQuery(parentDropdownQuery, {
    skip: !canAccessOrders
  });
  const { data: locationData, error: locationError } = useGetAllLocationsSimpleQuery(undefined, {
    skip: !canAccessOrders
  });
  const { data: promoCodesData, error: promoCodesError } = useGetAllPromotionalCodesQuery(undefined, {
    skip: !canAccessOrders
  });

  const filterFormFields: FilterField[] = [
    {
      name: 'id',
      label: 'ID',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'id')
    },
    {
      name: 'parent_id',
      type: 'number',
      element: (
        <FormSelect
          name="parent_id"
          label="Parent"
          options={
            parentData?.results?.map((parent: User) => ({
              value: parent.id,
              label: `${parent.first_name} ${parent.last_name}`
            })) ?? []
          }
          loadingError={parentError ? 'Error loading parents' : undefined}
          selected_Value={parentData?.results
            ?.map((parent: User) => ({
              value: parent.id,
              label: `${parent.first_name} ${parent.last_name}`
            }))
            .find(
              (option) =>
                String(option.value) === getFilterValue(filters.data?.filterBy, 'parent_id')
            )}
        />
      )
    },
    {
      name: 'course_id',
      type: 'number',
      element: (
        <FormSelect
          name="course_id"
          label="Session"
          options={
            coursesData?.results?.map((course: Course) => ({
              value: course.id,
              label: course.title
            })) ?? []
          }
          loadingError={coursesError ? 'Error loading sessions' : undefined}
          selected_Value={coursesData?.results
            ?.map((course: Course) => ({ value: course.id, label: course.title }))
            .find(
              (option) =>
                String(option.value) === getFilterValue(filters.data?.filterBy, 'course_id')
            )}
        />
      )
    },
    {
      name: 'parent_email',
      element: (
        <SearchField
          name="parent_email"
          label="Email"
          placeholder="Search parent email"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'parent_email')
    },
    {
      name: 'promotional_code_id',
      type: 'number',
      element: (
        <FormSelect
          name="promotional_code_id"
          label="Promotional Code"
          options={
            promoCodesData?.results?.map((promo: PromotionalCode) => ({
              value: promo.id,
              label: promo.name
            })) ?? []
          }
          loadingError={promoCodesError ? 'Error loading promotional codes' : undefined}
          selected_Value={promoCodesData?.results
            ?.map((promo: PromotionalCode) => ({ value: promo.id, label: promo.name }))
            .find(
              (option) =>
                String(option.value) ===
                getFilterValue(filters.data?.filterBy, 'promotional_code_id')
            )}
        />
      )
    },
    {
      name: 'created_at_from',
      id: 'created_at',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="created_at_from"
          label="Created At"
        />
      ),
      value: toCreatedAtDateInputValue(
        'greater_equal_than',
        getFilterValue(filters.data?.filterBy, 'created_at', 'greater_equal_than')
      )
    },
    {
      name: 'created_at_to',
      id: 'created_at',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="created_at_to" />,
      value: toCreatedAtDateInputValue(
        'lesser_equal_than',
        getFilterValue(filters.data?.filterBy, 'created_at', 'lesser_equal_than')
      )
    }
  ];

  if (currentUser?.isGlobal) {
    const locationOptions = locationData?.map((loc) => ({ value: loc.id, label: loc.title })) ?? [];
    filterFormFields.push({
      name: 'location_id',
      type: 'number',
      label: 'Location',
      element: (
          <FormSelect
              name="location_id"
              label="Location"
              options={locationOptions}
              loadingError={locationError ? 'Error loading locations' : undefined}
              selected_Value={locationOptions.find(
                  (option) =>
                      String(option.value) === getFilterValue(filters.data?.filterBy, 'location_id')
              )}
          />
      )
    });
  }

  useEffect(() => {
    if (!isLoading && error) {
      addToast('Error while loading orders', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading]);

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
          data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
          data.scopes.map((sMap) =>
            Object.entries(sMap).map(([scope, title]) => {
              let scope_count = 0;
              if (data.scope_counts != null) {
                scope_count = data.scope_counts[scope] ?? 0;
              }
              return {
                title: `${title} (${scope_count})`,
                key: scope
              };
            })
          )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setOrders(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    const normalizedFilterBy = filterBy.map((filter) =>
      filter.field === 'created_at'
        ? { ...filter, value: normalizeCreatedAtFilterValue(filter.operator, filter.value) }
        : filter
    );
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy: normalizedFilterBy
      }
    }));
  };

  // RBAC-FE-ORDERS: Defense-in-depth guard
  if (!canAccessOrders) {
    return <Forbidden />;
  }

  return (
    <div className="py-2">
      <SFCTable
        name={`${scopes[filters?.scope!] ?? ''} Orders`}
        columns={getOrdersColumns(filters.scope)}
        data={orders}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        scopes={buttons}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={filters.data?.filterBy}
        indexDownloadPath="orders"
        sidebars={
          canSeeFinanceSummary(currentUser)
            ? [
                <DetailSection title="Summary">
                  <LabelText label="Amount Paid:">
                    <CurrencyCell value={data?.totals?.total ?? 0} />
                  </LabelText>
                </DetailSection>
              ]
            : []
        }
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
      />
    </div>
  );
};
